import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { primeAudio, playAlertBeep } from "@/lib/beep";

const POLL_MS = 8000; // chat benefits from tighter polling than inquiries/appointments

// Same polling/watermark pattern as useInquiryAlerts and useAppointmentAlerts, keyed
// on conversation updated_at (a visitor follow-up bumps this even without a new conversation).
export function useChatAlerts(user) {
  const [conversations, setConversations] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [soundArmed, setSoundArmed] = useState(false);

  const lastSeenRef = useRef(null);
  const initializedRef = useRef(false);
  const storageKey = user ? `eazy_last_seen_chat_${user.email}` : null;

  useEffect(() => {
    if (!storageKey) return;
    lastSeenRef.current = localStorage.getItem(storageKey);
    initializedRef.current = false;
  }, [storageKey]);

  const armSound = useCallback(() => {
    setSoundArmed(primeAudio());
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get("/admin/chat/conversations");
        if (cancelled) return;
        const items = data.items || [];
        setConversations(items);

        const unreadNow = items.filter((c) => c.unread_by_staff);
        if (unreadNow.length > 0) {
          const newest = unreadNow.reduce((max, c) => (c.updated_at > max ? c.updated_at : max), unreadNow[0].updated_at);

          if (!initializedRef.current) {
            initializedRef.current = true;
            if (!lastSeenRef.current) {
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          } else {
            const freshOnes = lastSeenRef.current
              ? unreadNow.filter((c) => c.updated_at > lastSeenRef.current)
              : [];
            if (freshOnes.length > 0) {
              freshOnes.forEach((c) => {
                toast.success(`New chat message from ${c.visitor_name}`);
              });
              playAlertBeep();
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          }
        } else if (!initializedRef.current) {
          initializedRef.current = true;
        }
      } catch {
        // silent — avoid spamming errors from background polling
      } finally {
        if (!cancelled) setLoadingChats(false);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user, storageKey]);

  return { conversations, setConversations, loadingChats, soundArmed, armSound };
}
